// VendorPage.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import VendorListView from "./VendorListView";
import VendorKanbanView from "./VendorKanbanView";
import { useVendors } from "../../context/VendorContext";
import api, { API_BASE_URL } from '../../api/axiosConfig';
import Swal from 'sweetalert2';
import BulkImportControl from '../../components/common/BulkImportControl';
import { useAuth } from "../../context/AuthContext";
import RoleGate from "../../components/RoleGate";
import useModuleFilters from "../../hooks/useModuleFilters";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import {
  BuildingStorefrontIcon, CheckCircleIcon, XCircleIcon,
  ClockIcon
} from "@heroicons/react/24/outline";

const VendorPage = () => {
  const [view, setView] = useState("list");
  const {
    showFilter,
    setShowFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
    clearFilters,
    clearingFilters,
  } = useModuleFilters("vendors", {
    categories: [],
    statuses: [],
    ratings: [],
  });
  const [filters, setFilters] = useState({ status: "", type: "" });
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const { vendors, fetchVendors } = useVendors();
  const { checkAccess } = useAuth();
  const filterRef = useRef(null);

  useEffect(() => {
    if (!checkAccess('vendor.view')) {
      navigate('/admin/dashboard');
      return;
    }
    if (fetchVendors) fetchVendors();
  }, [checkAccess, navigate, fetchVendors]);

  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleClearAll = () => {
    setFilters({ status: "", type: "" });
    setSearchTerm("");
    if (fetchVendors) fetchVendors();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!'
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/vendors/${id}`);
        Swal.fire('Deleted!', 'The vendor has been deleted.', 'success').then(() => window.location.reload());
      } catch {
        Swal.fire('Error!', 'Failed to delete vendor.', 'error');
      }
    }
  };

  const filteredVendors = useMemo(() => {
    let result = [...vendors];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(v => v.name?.toLowerCase().includes(s) || v.category?.toLowerCase().includes(s));
    }
    if (filters.status) result = result.filter(v => v.status?.toLowerCase() === filters.status.toLowerCase());
    if (filters.type) result = result.filter(v => v.type?.toLowerCase() === filters.type.toLowerCase());
    return result;
  }, [vendors, searchTerm, filters]);

  // ── Stats from ALL vendors ───────────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Vendors",
      value: vendors.length,
      icon: BuildingStorefrontIcon,
      variant: "default",
      isActive: !filters.status && !filters.type,
      onClick: () => setFilters({ status: "", type: "" }),
    },
    {
      label: "Active",
      value: vendors.filter(v => v.status === "Active").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filters.status === "Active",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Active" ? "" : "Active" })),
    },
    {
      label: "Inactive",
      value: vendors.filter(v => v.status === "Inactive").length,
      icon: XCircleIcon,
      variant: "gray",
      isActive: filters.status === "Inactive",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Inactive" ? "" : "Inactive" })),
    },
    {
      label: "Pending",
      value: vendors.filter(v => v.status === "Pending").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: filters.status === "Pending",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Pending" ? "" : "Pending" })),
    },
  ], [vendors, filters.status, filters.type]);

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={BuildingStorefrontIcon}
        iconColor="text-[#E8612C]"
        title="Vendor Management"
        subtitle="Manage suppliers, contractors and service providers"
        onRefresh={() => window.location.reload()}
      >
        <RoleGate permission="vendor.manage">
          <BulkImportControl
            tableName="Vendors"
            columns={[
              { header: 'Vendor Name', sample: 'Tech Corp' },
              { header: 'Company Name', sample: 'Tech Solutions Ltd' },
              { header: 'Category', sample: 'Material Supplier' },
              { header: 'Email', sample: 'info@tech.com' },
              { header: 'Phone', sample: '9876543210' },
              { header: 'Address', sample: '123 Business Park, NY' },
              { header: 'Contact Person', sample: 'John Manager' },
              { header: 'GST No', sample: '22AAAAA0000A1Z5' },
              { header: 'PAN No', sample: 'ABCDE1234F' },
              { header: 'License No', sample: 'LIC-2024-99' },
              { header: 'Certificate Expiry', sample: '2025-12-31' },
              { header: 'Contract Start', sample: '2024-01-01' },
              { header: 'Contract End', sample: '2025-01-01' },
              { header: 'Rating', sample: '5' },
              { header: 'Performance', sample: 'Excellent service' },
              { header: 'Status', sample: 'Active' },
            ]}
            apiEndpoint={`${API_BASE_URL}/api/vendors/bulk-import`}
            onRefresh={() => window.location.reload()}
          />
        </RoleGate>
        <RoleGate permission="vendor.manage">
          <TopPrimaryButton
            onClick={() => navigate("/admin/VendorPage/add")}
            label="Add Vendor"
          />
        </RoleGate>
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search vendors..."
        tabs={['All', 'Active', 'Inactive', 'Pending']}
        activeTab={filters.status || 'All'}
        onTabChange={(tab) => setFilters(f => ({ ...f, status: tab === 'All' ? '' : tab }))}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher
          view={view}
          onViewChange={setView}
          availableViews={['list', 'kanban']}
        />
      </Toolbar>

      {/* Filter panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        isClearing={clearingFilters}
        onClear={handleClearAll}
      >
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Vendor Category / Type</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            value={filters.type} 
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            disabled={loadingFilters}
            className="border border-gray-300 dark:border-[#30363d] rounded-lg p-2 w-full text-xs bg-white dark:bg-[#0d1117] text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>All Categories</T></option>
            {(filterOptions.categories?.length > 0 ? filterOptions.categories : ["Material", "Service", "Contractor"]).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Content */}
      <TableCard>
        {filteredVendors.length === 0 ? (
          <div className="py-10 text-center text-gray-500 dark:text-slate-400 flex flex-col items-center gap-2">
            <BuildingStorefrontIcon className="w-10 h-10 text-gray-300 dark:text-slate-600" />
            <p className="text-xs sm:text-sm"><T>No vendors found. Try adjusting filters.</T></p>
          </div>
        ) : view === "list" ? (
          <VendorListView search={searchTerm} filter={filters} vendors={filteredVendors} onDelete={handleDelete} />
        ) : (
          <VendorKanbanView search={searchTerm} filter={filters} vendors={filteredVendors} onDelete={handleDelete} />
        )}
      </TableCard>
    </PageLayout>
  );
};

export default VendorPage;